import { useContext, useCallback } from 'react';
import { pick } from 'lodash';
import { Button, Box, Typography } from '@mui/material/';
import { useAPI } from 'src/services/api';
import ToastContext from 'src/store/toast-context';
import {
  Download as DownloadIcon,
  OpenInNew as RedirectIcon,
  Add as AddIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { GridToolbarQuickFilter } from '@mui/x-data-grid';

import DataTableWrapper from 'src/components/DataTableWrapper';
import formatter from 'src/util/currencyFormatter';
import getStatusChip from 'src/util/getStatusChip';
import { Grid } from '@material-ui/core';
import AssetTableContext from 'src/store/asset-table-context';
import handleNull from 'src/util/handleNull';
import { colors } from 'src/styles/theme';

const formatCurrency = (item) => formatter.format(item.value);

const AssetToolbar = ({
  downloadSpreadsheet,
  openPDFButton,
  uploadModalController,
  assetModalController,
}) => {
  return (
    <Grid container display="flex" justifyContent="flex-end" width="199%">
      <Grid
        item
        xs={12}
        sx={{
          p: 0.5,
          pb: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            mt: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}>
            Equipment
          </Typography>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Button onClick={downloadSpreadsheet} startIcon={<DownloadIcon />}>
              Download Spreadsheet
            </Button>
            <Button onClick={openPDFButton} startIcon={<RedirectIcon />}>
              Percentage Good Rate
            </Button>
            <Button
              variant="outlined"
              onClick={uploadModalController.openModal}
              startIcon={<UploadIcon />}
            >
              Excel Import
            </Button>
            <Button
              data-cy="add-asset-btn"
              variant="outlined"
              onClick={assetModalController.openModal}
              startIcon={<AddIcon />}
            >
              Add Asset
            </Button>
          </Box>
        </Box>
        <GridToolbarQuickFilter
          sx={{
            width: '100%',
            backgroundColor: colors.white,
            border: `1px solid ${colors.grey[400]}`,
            borderRadius: '4px',
            px: 2,
            py: 0.5,
            boxShadow: `0px 1px 4px 0px ${colors.backdrop.shadowMd}`,
            '& .MuiInput-underline:before': {
              borderBottom: 'none',
            },
            '& .MuiInput-underline:after': {
              borderBottom: 'none',
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
              color: colors.grey[500],
            },
            '& .MuiInputBase-root': {
              width: '100%',
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

const AssetDataTable = () => {
  const api = useAPI();
  const { handleError } = useContext(ToastContext);
  const { assetModal, uploadModal, assetInfo } = useContext(AssetTableContext);
  const { assets, setInitialValues, setUpdating } = assetInfo;
  const { assetModalController } = assetModal;
  const { uploadModalController } = uploadModal;

  const downloadSpreadsheet = useCallback(async () => {
    try {
      const res = await api.get('/assets/spreadsheet', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assets.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error(err);
      handleError(err);
    }
  }, [api, handleError]);

  const openPDFButton = useCallback(() => {
    window.open(
      'https://assessor.utahcounty.gov/Documents/Percentage%20Good%20Rate%20Table.pdf',
      '_blank'
    );
  }, []);

  const columns = [
    {
      field: 'addDel',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 100,
      renderCell: getStatusChip,
    },
    {
      field: 'assetId',
      headerName: 'Asset #',
      flex: 0.5,
      minWidth: 70,
    },
    {
      field: 'propCode',
      headerName: 'Code',
      flex: 0.5,
      minWidth: 60,
    },
    {
      field: 'yearAcq',
      headerName: 'Year Acquired',
      flex: 1,
      minWidth: 110,
    },
    {
      field: 'itemDescr',
      headerName: 'Description',
      flex: 3,
      minWidth: 280,
    },
    {
      field: 'purchaseCost',
      headerName: 'Cost',
      valueFormatter: formatCurrency,
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: 'quantity',
      headerName: 'Quanitity',
      flex: 0.75,
      minWidth: 80,
    },
    {
      field: 'rate',
      headerName: 'Rate',
      flex: 0.5,
      minWidth: 60,
    },
    {
      field: 'marketVal',
      headerName: 'Market Value',
      valueFormatter: formatCurrency,
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'exemptFlag',
      headerName: 'Exempt',
      valueGetter: (params) => (params.row.exemptFlag ? 'Yes' : 'No'),
      flex: 0.75,
      minWidth: 75,
    },
    {
      field: 'lesseeName',
      headerName: 'Lessee Name',
      flex: 1,
      minWidth: 180,
    },
  ];

  const handleRowClick = ({ row }) => {
    let existingValues = pick(row, [
      'assetId',
      'itemDescr',
      'propCode',
      'purchaseCost',
      'quantity',
      'yearAcq',
      'lesseeAddr',
      'lesseeCity',
      'lesseeName',
    ]);
    // Santize any old class 28s
    if (existingValues.propCode === '28') {
      existingValues.propCode = '';
    }
    // Set isLeased checkbox if any lessee fields have data
    existingValues.isLeased = !!(row.lesseeName || row.lesseeAddr || row.lesseeCity);
    setUpdating(handleNull(row));
    setInitialValues(handleNull(existingValues));

    assetModalController.openModal();
  };

  const args = {
    components: {
      toolbar: AssetToolbar,
    },
    componentsProps: {
      toolbar: {
        downloadSpreadsheet,
        openPDFButton,
        uploadModalController,
        assetModalController,
      },
    },
    title: 'Assets',
    rows: assets,
    columns: columns,
    rowClick: handleRowClick,
    height: 830,
  };

  return <DataTableWrapper {...args} />;
};

export default AssetDataTable;
